import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Table,
} from "@themesberg/react-bootstrap";
import {
  FiCheckCircle,
  FiCreditCard,
  FiRefreshCw,
  FiShoppingBag,
  FiUsers,
} from "react-icons/fi";
import {
  adminAllCourseList,
  createCollaborativePurchaseOrder,
  previewCollaborativePurchase,
  userMySubIdsList,
  verifyCollaborativePurchasePayment,
} from "../../api";
import { Routes } from "../../../routes";
import "./userCollaborativePurchase.css";

const formatCurrency = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;

const loadRazorpayScript = () =>
  new Promise((resolve, reject) => {
    if (window.Razorpay) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Razorpay"));
    document.body.appendChild(script);
  });

const getAuthUser = () => {
  try {
    return JSON.parse(sessionStorage.getItem("authUser") || "{}");
  } catch {
    return {};
  }
};

export default function UserCollaborativePurchase() {
  const authUser = getAuthUser();
  const isSubIdAccount = authUser?.is_sub_id === true;

  const [loadingIds, setLoadingIds] = useState(true);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [subIds, setSubIds] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [selectedCourseIds, setSelectedCourseIds] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [paying, setPaying] = useState(false);
  const [result, setResult] = useState(null);

  const mainUserOption = useMemo(
    () => ({
      id: authUser?.id,
      custom_user_id: authUser?.custom_user_id,
      name: authUser?.name || "Main ID",
      username: authUser?.username,
      status: authUser?.status || "active",
      is_main: true,
    }),
    [authUser]
  );

  const fetchSubIds = useCallback(async () => {
    try {
      setLoadingIds(true);
      const response = await userMySubIdsList({ per_page: 100 });
      const ids = response?.data?.data?.ids || [];
      setSubIds(Array.isArray(ids) ? ids : []);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to load Sub IDs");
      setSubIds([]);
    } finally {
      setLoadingIds(false);
    }
  }, []);

  const fetchCourses = useCallback(async () => {
    try {
      setLoadingCourses(true);
      const response = await adminAllCourseList({ per_page: 100, status: "active" });
      const list =
        response?.data?.data?.courses ||
        response?.data?.data?.data ||
        response?.data?.data ||
        [];
      const mapped = (Array.isArray(list) ? list : []).map((c) => ({
        id: c.id,
        title: c.title || c.name,
        price: Number(c.price || c.total_price || 0),
        status: c.status,
        can_purchase: c.can_purchase !== false,
      }));
      setCourses(mapped.filter((c) => String(c.status || "active").toLowerCase() === "active"));
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to load courses");
      setCourses([]);
    } finally {
      setLoadingCourses(false);
    }
  }, []);

  useEffect(() => {
    if (!isSubIdAccount) {
      fetchSubIds();
      fetchCourses();
    }
  }, [isSubIdAccount, fetchSubIds, fetchCourses]);

  const toggleUser = (userId) => {
    setResult(null);
    setPreview(null);
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const toggleCourse = (courseId) => {
    setResult(null);
    setPreview(null);
    setSelectedCourseIds((prev) =>
      prev.includes(courseId)
        ? prev.filter((id) => id !== courseId)
        : [...prev, courseId]
    );
  };

  const handlePreview = async () => {
    if (!selectedUserIds.length || !selectedCourseIds.length) {
      toast.error("Select at least one ID and one course.");
      return;
    }

    try {
      setPreviewLoading(true);
      setResult(null);
      const response = await previewCollaborativePurchase({
        user_ids: selectedUserIds,
        course_ids: selectedCourseIds,
      });
      setPreview(response?.data?.data || null);
      toast.success("Preview ready.");
    } catch (error) {
      setPreview(null);
      toast.error(error?.response?.data?.message || "Unable to preview purchase.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handlePay = async () => {
    if (!preview?.summary?.total_amount) {
      toast.error("Generate a preview before paying.");
      return;
    }

    try {
      setPaying(true);
      const orderResponse = await createCollaborativePurchaseOrder({
        user_ids: selectedUserIds,
        course_ids: selectedCourseIds,
      });
      const orderData = orderResponse?.data?.data;
      const rzp = orderData?.razorpay_order;

      if (!rzp?.id || !rzp?.key_id) {
        throw new Error("Invalid payment order response.");
      }

      await loadRazorpayScript();

      await new Promise((resolve, reject) => {
        const options = {
          key: rzp.key_id,
          amount: rzp.amount,
          currency: rzp.currency || "INR",
          name: "LifeSkillGlobal",
          description: "Collaborative Course Purchase",
          order_id: rzp.id,
          prefill: orderData.prefill || {},
          theme: { color: "#1B3C6E" },
          handler: async (paymentResponse) => {
            try {
              const verifyResponse = await verifyCollaborativePurchasePayment({
                purchase_group_id: orderData.purchase_group_id,
                transaction_id: orderData.transaction_id,
                razorpay_order_id: paymentResponse.razorpay_order_id,
                razorpay_payment_id: paymentResponse.razorpay_payment_id,
                razorpay_signature: paymentResponse.razorpay_signature,
              });

              const data = verifyResponse?.data?.data || verifyResponse?.data;
              setResult(data);
              setPreview(null);
              toast.success(verifyResponse?.data?.message || "Payment successful.");
              resolve();
            } catch (verifyError) {
              toast.error(
                verifyError?.response?.data?.message || "Payment verification failed."
              );
              reject(verifyError);
            }
          },
          modal: {
            ondismiss: () => {
              toast.info("Payment cancelled. No charge was completed.");
              resolve();
            },
          },
        };

        const razorpay = new window.Razorpay(options);
        razorpay.on("payment.failed", (response) => {
          toast.error(
            response?.error?.description ||
              response?.error?.reason ||
              "Payment failed. Please try again."
          );
          resolve();
        });
        razorpay.open();
      });
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Unable to start payment.");
    } finally {
      setPaying(false);
    }
  };

  if (isSubIdAccount) {
    return (
      <Card className="border-0 shadow-sm rounded-4 mt-4">
        <Card.Body className="p-5 text-center">
          <h4 className="mb-2">Group Purchase</h4>
          <p className="text-muted mb-0">
            Sub-ID accounts cannot create group purchases. Please log in with your primary account.
          </p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <div className="group-purchase-page py-4">
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
        <div>
          <h2 className="h4 mb-1">Group Purchase</h2>
          <p className="text-muted mb-0 small">
            Select Main/Sub IDs and courses, pay once, and get one combined invoice.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={() => {
            fetchSubIds();
            fetchCourses();
          }}
        >
          <FiRefreshCw className="me-1" /> Refresh
        </Button>
      </div>

      {result?.invoice && (
        <Card className="border-0 shadow-sm rounded-4 mb-4 group-purchase-success">
          <Card.Body>
            <div className="d-flex align-items-start gap-3">
              <FiCheckCircle size={28} className="text-success mt-1" />
              <div className="flex-grow-1">
                <h5 className="mb-1">Payment successful</h5>
                <p className="text-muted small mb-2">
                  Purchase group #{result.purchase_group_id} · Invoice{" "}
                  <strong>{result.invoice.invoice_number}</strong>
                </p>
                <div className="d-flex flex-wrap gap-2">
                  <Button
                    as={Link}
                    to={Routes.UserInvoices.path}
                    size="sm"
                    className="group-purchase-btn-primary"
                  >
                    View Invoices
                  </Button>
                  <Button
                    as={Link}
                    to={`${Routes.UserInvoices.path}`}
                    size="sm"
                    variant="outline-primary"
                  >
                    Invoice #{result.invoice.invoice_number}
                  </Button>
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      <Row className="g-3 mb-3">
        <Col lg={6}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body>
              <div className="d-flex align-items-center gap-2 mb-3">
                <FiUsers />
                <h6 className="mb-0 fw-bold">Step 1 · Select IDs</h6>
              </div>
              {loadingIds ? (
                <div className="text-muted py-4 text-center">
                  <Spinner size="sm" className="me-2" /> Loading IDs...
                </div>
              ) : (
                <div className="group-purchase-checklist">
                  <label className="group-purchase-check-item">
                    <Form.Check
                      type="checkbox"
                      checked={selectedUserIds.includes(Number(mainUserOption.id))}
                      onChange={() => toggleUser(Number(mainUserOption.id))}
                      disabled={!mainUserOption.id}
                    />
                    <div>
                      <div className="fw-semibold">
                        {mainUserOption.name}{" "}
                        <Badge bg="primary" className="ms-1">
                          Main ID
                        </Badge>
                      </div>
                      <div className="small text-muted">
                        {mainUserOption.custom_user_id || "—"}
                      </div>
                    </div>
                  </label>

                  {subIds.length === 0 ? (
                    <div className="text-muted small py-2">No Sub IDs found.</div>
                  ) : (
                    subIds.map((item) => (
                      <label key={item.id || item.custom_user_id} className="group-purchase-check-item">
                        <Form.Check
                          type="checkbox"
                          checked={selectedUserIds.includes(Number(item.id))}
                          onChange={() => toggleUser(Number(item.id))}
                          disabled={String(item.status).toLowerCase() === "blocked"}
                        />
                        <div>
                          <div className="fw-semibold">
                            {item.name}{" "}
                            <Badge
                              bg={
                                String(item.status).toLowerCase() === "active"
                                  ? "success"
                                  : String(item.status).toLowerCase() === "blocked"
                                    ? "danger"
                                    : "secondary"
                              }
                              className="ms-1"
                            >
                              {item.status || "inactive"}
                            </Badge>
                          </div>
                          <div className="small text-muted">
                            {item.custom_user_id}
                            {item.username ? ` · @${item.username}` : ""}
                          </div>
                        </div>
                      </label>
                    ))
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body>
              <div className="d-flex align-items-center gap-2 mb-3">
                <FiShoppingBag />
                <h6 className="mb-0 fw-bold">Step 2 · Select Courses</h6>
              </div>
              {loadingCourses ? (
                <div className="text-muted py-4 text-center">
                  <Spinner size="sm" className="me-2" /> Loading courses...
                </div>
              ) : courses.length === 0 ? (
                <div className="text-muted small">No active courses available.</div>
              ) : (
                <div className="group-purchase-checklist">
                  {courses.map((course) => (
                    <label key={course.id} className="group-purchase-check-item">
                      <Form.Check
                        type="checkbox"
                        checked={selectedCourseIds.includes(Number(course.id))}
                        onChange={() => toggleCourse(Number(course.id))}
                      />
                      <div className="flex-grow-1">
                        <div className="fw-semibold">{course.title}</div>
                        <div className="small text-muted">
                          {formatCurrency(course.price)} (GST inclusive)
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="border-0 shadow-sm rounded-4 mb-3">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <div className="d-flex align-items-center gap-2">
              <FiCreditCard />
              <h6 className="mb-0 fw-bold">Step 3 · Preview & Pay</h6>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <Button
                className="group-purchase-btn-outline"
                onClick={handlePreview}
                disabled={previewLoading || paying}
              >
                {previewLoading ? (
                  <>
                    <Spinner size="sm" className="me-2" /> Previewing...
                  </>
                ) : (
                  "Generate Preview"
                )}
              </Button>
              <Button
                className="group-purchase-btn-primary"
                onClick={handlePay}
                disabled={!preview || paying || previewLoading}
              >
                {paying ? (
                  <>
                    <Spinner size="sm" className="me-2" /> Processing...
                  </>
                ) : (
                  `Pay Now${preview ? ` · ${formatCurrency(preview.summary?.total_amount)}` : ""}`
                )}
              </Button>
            </div>
          </div>

          {!preview ? (
            <div className="text-muted small">
              Selected {selectedUserIds.length} ID(s) and {selectedCourseIds.length} course(s).
              Generate preview to see line items and totals from the server.
            </div>
          ) : (
            <>
              <div className="table-responsive mb-3">
                <Table hover className="align-middle mb-0 group-purchase-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>User</th>
                      <th>Course</th>
                      <th>Base</th>
                      <th>GST</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(preview.items || []).map((item, index) => (
                      <tr key={`${item.user_id}-${item.course_id}`}>
                        <td>{index + 1}</td>
                        <td>
                          <div className="fw-semibold">{item.name}</div>
                          <div className="small text-muted">{item.custom_user_id}</div>
                        </td>
                        <td>{item.course_name}</td>
                        <td>{formatCurrency(item.base_amount)}</td>
                        <td>
                          {formatCurrency(item.gst_amount)}
                          <div className="small text-muted">{item.gst_percentage}%</div>
                        </td>
                        <td className="fw-semibold">{formatCurrency(item.total_amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              <div className="group-purchase-totals">
                <div>
                  <span className="label">Items</span>
                  <strong>{preview.summary?.total_items ?? 0}</strong>
                </div>
                <div>
                  <span className="label">Base</span>
                  <strong>{formatCurrency(preview.summary?.total_base_amount)}</strong>
                </div>
                <div>
                  <span className="label">GST</span>
                  <strong>{formatCurrency(preview.summary?.total_gst_amount)}</strong>
                </div>
                <div>
                  <span className="label">Grand Total</span>
                  <strong>{formatCurrency(preview.summary?.total_amount)}</strong>
                </div>
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}
